import { isEmpty } from 'lodash/fp';
import { batch } from 'react-redux';
import { CommanderThunkAction, Interval } from '@mews-commander/types';
import { compactArray } from '@mews/fp';
import { ActionLogItemEntityFilter, ActionLogItemScopeType } from '@mews-commander/data-access';
import { chunkIntervalIntoMonths } from '@mews-commander/utils';
import { setActionLogItems } from '../ducks/actionLogItems';
import { setProfiles } from '../ducks/profiles';

interface FetchActionLogItemProps {
    enterpriseIds: string[];
    chainIds?: string[];
    userIds?: string[];
    createdUtc: Interval;
    entityFilters: ActionLogItemEntityFilter[];
}

export const fetchActionLogItems =
    ({ enterpriseIds, createdUtc, entityFilters }: FetchActionLogItemProps): CommanderThunkAction =>
    async (dispatch, getState, { ensureQueryData }) => {
        const chunkedCreatedUtcIntervals = chunkIntervalIntoMonths(createdUtc);

        const scope = {
            discriminator: ActionLogItemScopeType.Enterprise as const,
            value: { enterpriseIds },
        };

        const fetchActionLogItemsChunk = async (createdUtcChunk: Interval) =>
            /**
             * This stores the fetched data in react-query cache as if we used useQuery.
             * The data is then used in useActionLogData, which we need to use profiles and users in nested components.
             * */
            ensureQueryData('actionLogItems', 'getAll', {
                createdUtc: createdUtcChunk,
                entityFilters,
                // @ts-expect-error Coproducts are not annotated correctly: https://github.com/MewsSystems/mews/pull/37114
                scope,
            });
        return Promise.all(chunkedCreatedUtcIntervals.map(fetchActionLogItemsChunk)).then((result) => {
            const actionLogItemsArray = compactArray(result.flatMap(({ actionLogItems }) => actionLogItems));
            const profilesArray = compactArray(result.flatMap(({ profiles }) => profiles));

            if (!isEmpty(actionLogItemsArray)) {
                batch(() => {
                    dispatch(setActionLogItems(actionLogItemsArray));
                    dispatch(setProfiles(profilesArray));
                });
            }
        });
    };
